import React, { useMemo, useState, useEffect } from 'react';
import type { Dayjs } from 'dayjs';
import { EchartsWithTheme, useTheme, Empty, Select } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import {
  apiV2ReportDataEnergyReportCostPost,
  apiV2ReportDataEnergyReportDeptCostPost,
  apiV2ReportDataEnergyReportAreaCostPost,
  apiV2ReportDataEnergyReportEnergyTrendPost,
  apiV2ReportDataEnergyReportDeptConsumptionPost,
  apiV2ReportDataEnergyReportAreaConsumptionPost,
  apiV2ReportDataEnergyReportEnergyMediumListPost,
} from '@maxtropy/device-customer-apis-v2';
import { keepTwoAndWanOrLine } from '@/utils/utils';
import { ReportType, getReportTimeRange } from '../../const';
import CardBox from '../CardBox';
import RankingCard from '../RankingCard';
import { getTrendChartOptionMonthly, TrendCategoryItem } from './charts';
import styles from './index.module.scss';

interface EnergyTrendProps {
  reportType: ReportType;
  selectedDate: Dayjs;
}

const EnergyTrend: React.FC<EnergyTrendProps> = ({ selectedDate }) => {
  const theme = useTheme();

  // 三个独立的介质选择状态
  const [selectedMediumId, setSelectedMediumId] = useState<string | undefined>(undefined);
  const [deptMediumId, setDeptMediumId] = useState<string | undefined>(undefined);
  const [areaMediumId, setAreaMediumId] = useState<string | undefined>(undefined);

  // 获取介质列表
  const { data: mediumListData } = useRequest(() => apiV2ReportDataEnergyReportEnergyMediumListPost({}));

  // 介质选项
  const mediumOptions = useMemo(() => {
    return (mediumListData?.list || []).map(item => ({
      label: item.name || '',
      value: String(item.id),
    }));
  }, [mediumListData]);

  // 默认选择 id 为 2030 的介质，如果没有则选择第一个
  useEffect(() => {
    if (mediumOptions.length > 0) {
      // 查找 id 为 2030 的介质
      const defaultMedium = mediumOptions.find(item => item.value === '2030');
      const defaultValue = defaultMedium?.value ?? mediumOptions[0].value;

      if (selectedMediumId === undefined) setSelectedMediumId(defaultValue);
      if (deptMediumId === undefined) setDeptMediumId(defaultValue);
      if (areaMediumId === undefined) setAreaMediumId(defaultValue);
    }
  }, [mediumOptions]);

  // 各下拉框当前选中介质的名称
  const currentMediumName = useMemo(() => {
    const medium = mediumListData?.list?.find(item => String(item.id) === selectedMediumId);
    return medium?.name || '';
  }, [mediumListData, selectedMediumId]);

  // 构建基础请求参数（月报模式）
  const baseRequestParams = useMemo(() => {
    const range = getReportTimeRange(ReportType.MONTHLY, selectedDate);
    return {
      fromTime: range.fromTime,
      toTime: range.toTime,
      timeResolution: 'MONTH' as const,
    };
  }, [selectedDate]);

  // 全厂用能成本接口
  const { data: costData, loading: costLoading } = useRequest(
    () => apiV2ReportDataEnergyReportCostPost(baseRequestParams),
    { refreshDeps: [baseRequestParams] }
  );

  // 部门用能成本排名接口
  const { data: deptCostData, loading: deptCostLoading } = useRequest(
    () => apiV2ReportDataEnergyReportDeptCostPost(baseRequestParams),
    { refreshDeps: [baseRequestParams] }
  );

  // 区域用能成本排名接口
  const { data: areaCostData, loading: areaCostLoading } = useRequest(
    () => apiV2ReportDataEnergyReportAreaCostPost(baseRequestParams),
    { refreshDeps: [baseRequestParams] }
  );

  // 全厂用能趋势接口（传 energyMediumId）
  const { data: energyTrendData, loading: energyTrendLoading } = useRequest(
    () =>
      apiV2ReportDataEnergyReportEnergyTrendPost({
        ...baseRequestParams,
        energyMediumId: selectedMediumId ? Number(selectedMediumId) : undefined,
      }),
    {
      refreshDeps: [baseRequestParams, selectedMediumId],
      ready: selectedMediumId !== undefined,
    }
  );

  // 部门能耗排名接口（使用独立的 deptMediumId）
  const { data: deptConsumptionData, loading: deptConsumptionLoading } = useRequest(
    () =>
      apiV2ReportDataEnergyReportDeptConsumptionPost({
        ...baseRequestParams,
        energyMediumId: deptMediumId ? Number(deptMediumId) : undefined,
      }),
    {
      refreshDeps: [baseRequestParams, deptMediumId],
      ready: deptMediumId !== undefined,
    }
  );

  // 区域能耗排名接口（使用独立的 areaMediumId）
  const { data: areaConsumptionData, loading: areaConsumptionLoading } = useRequest(
    () =>
      apiV2ReportDataEnergyReportAreaConsumptionPost({
        ...baseRequestParams,
        energyMediumId: areaMediumId ? Number(areaMediumId) : undefined,
      }),
    {
      refreshDeps: [baseRequestParams, areaMediumId],
      ready: areaMediumId !== undefined,
    }
  );

  // 成本趋势数据转换
  const costTrendData = useMemo(() => {
    if (!costData?.currentMonth) return [];

    const { currentMonth, lastMonth, lastYearSameMonth } = costData;

    return (currentMonth?.map(item => {
      const dateStr = item.date || '';
      // X轴显示日期最后两位（如 "01"、"31"）
      const day = dateStr.slice(8) || '';
      // 本月日期 MM-DD（如 "01-15"）
      const currentDate = dateStr.slice(5) || '';
      // 按"日"匹配上月数据（如31号对应上月31号）
      const lastMonthItem = lastMonth?.find(lm => lm.date?.slice(8) === day);
      const lastMonthDate = lastMonthItem?.date?.slice(5) || '--';
      // 按"日"匹配去年同期数据
      const lastYearItem = lastYearSameMonth?.find(ly => ly.date?.slice(8) === day);
      const lastYearDate = lastYearItem?.date || '--';

      return {
        day,
        currentDate,
        lastMonthDate,
        lastYearDate,
        current: item.value,
        lastMonth: lastMonthItem?.value,
        lastYear: lastYearItem?.value,
      };
    }) || []) as TrendCategoryItem[];
  }, [costData]);

  // 用能趋势数据转换
  const energyTrendList = useMemo(() => {
    if (!energyTrendData?.currentMonth) return [];

    const { currentMonth, lastMonth, lastYearSameMonth } = energyTrendData;

    return (currentMonth?.map(item => {
      const dateStr = item.date || '';
      // X轴显示日期最后两位（如 "01"、"31"）
      const day = dateStr.slice(8) || '';
      // 本月日期 MM-DD（如 "01-15"）
      const currentDate = dateStr.slice(5) || '';
      // 按"日"匹配上月数据（如31号对应上月31号）
      const lastMonthItem = lastMonth?.find(lm => lm.date?.slice(8) === day);
      const lastMonthDate = lastMonthItem?.date?.slice(5) || '--';
      // 按"日"匹配去年同期数据
      const lastYearItem = lastYearSameMonth?.find(ly => ly.date?.slice(8) === day);
      const lastYearDate = lastYearItem?.date || '--';

      return {
        day,
        currentDate,
        lastMonthDate,
        lastYearDate,
        current: item.value,
        lastMonth: lastMonthItem?.value,
        lastYear: lastYearItem?.value,
      };
    }) || []) as TrendCategoryItem[];
  }, [energyTrendData]);

  // 部门用能成本排名数据转换
  const deptCostRankData = useMemo(() => {
    const list = deptCostData?.list ?? [];
    if (list.length === 0) return [];

    return list.map(item => ({
      name: item.deptName || '--',
      value: item.cost,
      percent: item.ratio ?? 0,
    }));
  }, [deptCostData]);

  // 区域用能成本排名数据转换
  const areaCostRankData = useMemo(() => {
    const list = areaCostData?.list ?? [];
    if (list.length === 0) return [];

    return list.map(item => ({
      name: item.areaName || '--',
      value: item.cost,
      percent: item.ratio ?? 0,
    }));
  }, [areaCostData]);

  // 部门能耗排名数据转换
  const deptConsumptionRankData = useMemo(() => {
    const list = deptConsumptionData?.list ?? [];
    if (list.length === 0) return [];

    return list.map(item => ({
      name: item.deptName || '--',
      value: item.consumption,
      percent: item.ratio ?? 0,
    }));
  }, [deptConsumptionData]);

  // 区域能耗排名数据转换
  const areaConsumptionRankData = useMemo(() => {
    const list = areaConsumptionData?.list ?? [];
    if (list.length === 0) return [];

    return list.map(item => ({
      name: item.areaName || '--',
      value: item.consumption,
      percent: item.ratio ?? 0,
    }));
  }, [areaConsumptionData]);

  // 部门能耗单位：从介质列表中获取当前选中介质的单位
  const deptMediumUnit = useMemo(() => {
    const medium = mediumListData?.list?.find(item => String(item.id) === deptMediumId);
    return medium?.unitName || '--';
  }, [mediumListData, deptMediumId]);

  // 区域能耗单位：从介质列表中获取当前选中介质的单位
  const areaMediumUnit = useMemo(() => {
    const medium = mediumListData?.list?.find(item => String(item.id) === areaMediumId);
    return medium?.unitName || '--';
  }, [mediumListData, areaMediumId]);

  // 全厂用量趋势单位：从介质列表中获取当前选中介质的单位
  const currentMediumUnit = useMemo(() => {
    const medium = mediumListData?.list?.find(item => String(item.id) === selectedMediumId);
    return medium?.unitName || '--';
  }, [mediumListData, selectedMediumId]);

  // 图表配置
  const costChartOption = useMemo(
    () => getTrendChartOptionMonthly(costTrendData as TrendCategoryItem[], '元'),
    [costTrendData]
  );

  const usageChartOption = useMemo(
    () => getTrendChartOptionMonthly(energyTrendList as TrendCategoryItem[], currentMediumUnit),
    [energyTrendList, currentMediumUnit]
  );

  return (
    <CardBox title="用能趋势">
      <div className={styles.chart_row}>
        <div className={styles.chart_col}>
          <div className={styles.section_title}>全厂用能成本</div>
          <div className={styles.chart_container}>
            <EchartsWithTheme
              theme={theme}
              loading={costLoading}
              empty={{ isShowEmpty: costTrendData.length === 0, emptyNode: <Empty /> }}
              option={costChartOption}
              style={{ height: 300 }}
            />
          </div>
        </div>
        <div className={styles.ranking_col}>
          <RankingCard
            title="部门用能成本排名"
            data={deptCostRankData}
            unit="元"
            showValue
            showPercent
            valueFormatter={v => keepTwoAndWanOrLine(v)}
          />
        </div>
        <div className={styles.ranking_col}>
          <RankingCard
            title="区域用能成本排名"
            data={areaCostRankData}
            unit="元"
            showValue
            showPercent
            valueFormatter={v => keepTwoAndWanOrLine(v)}
          />
        </div>
      </div>

      <div className={styles.chart_row} style={{ marginTop: 16 }}>
        <div className={styles.chart_col}>
          <div className={styles.section_title_wrapper}>
            <span className={styles.section_title}>全厂用量趋势</span>
            <Select
              value={selectedMediumId}
              onChange={setSelectedMediumId}
              options={mediumOptions}
              style={{ width: 120, marginLeft: 12 }}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className={styles.chart_container}>
            <EchartsWithTheme
              theme={theme}
              loading={energyTrendLoading}
              empty={{ isShowEmpty: energyTrendList.length === 0, emptyNode: <Empty /> }}
              option={usageChartOption}
              style={{ height: 300 }}
            />
          </div>
        </div>
        <div className={styles.ranking_col}>
          <RankingCard
            title="部门能耗排名"
            data={deptConsumptionRankData}
            filterOptions={mediumOptions}
            selectedFilter={deptMediumId}
            onFilterChange={setDeptMediumId}
            unit={deptMediumUnit}
            showValue
            showPercent
            valueColor="#FFCB47"
            valueFormatter={v => keepTwoAndWanOrLine(v)}
          />
        </div>
        <div className={styles.ranking_col}>
          <RankingCard
            title="区域能耗排名"
            data={areaConsumptionRankData}
            filterOptions={mediumOptions}
            selectedFilter={areaMediumId}
            onFilterChange={setAreaMediumId}
            unit={areaMediumUnit}
            showValue
            showPercent
            valueColor="#FFCB47"
            valueFormatter={v => keepTwoAndWanOrLine(v)}
          />
        </div>
      </div>
    </CardBox>
  );
};

export default EnergyTrend;
